// Figma: node 18034:1563 ("Campaigns" table).
// LOGIC PORTED from LC2's CampaignsTable.js (commons/components/HomeTabs/Today/Campaign/):
// same row-building from `entities.campaigns` (community lookup, "all_actions" -> offers
// count, "target" -> targets list), same row-click navigation to the campaign route.
// Not ported yet: `findActiveOffersChipValue` filtering out legacy-action offers (needs
// campaign `all_actions` shape from a live campaign fetch we haven't wired into a detail
// view yet) — Offers column falls back to a plain count for now.
// Reused by the Communities tab (see pages/Dashboard/Communities/CommunitySection.js) —
// LC2's CommunityTab passed a pre-filtered `campaigns.filter(c => c.property_sfid ===
// dashboardCommunity.sfid)` list as a `data` prop into its own CampaignsTable instance.
// Here that filtering is done internally off `params.community_sfid` instead (same
// self-contained route-based-filtering convention already used by ResidentActionFeedTable
// and AlertsSection in this folder), so this one component serves both routes.
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {Stack, Typography} from '@mui/material';

import {getCampaigns, getCommunities} from '../../app/store/reducers/entities';
import DataTableShell from './components/DataTableShell';
import Badge from './components/Badge';

const CampaignsTable = () => {
    const navigate = useNavigate();
    const params = useParams();
    const campaigns = useSelector(getCampaigns);
    const communities = useSelector(getCommunities);

    const rows = useMemo(() => {
        const scopedCampaigns = params.community_sfid
            ? campaigns.filter((campaign) => campaign.property_sfid === params.community_sfid)
            : campaigns;

        return [...scopedCampaigns]
            .map((campaign) => {
                const community = communities.find((c) => c.sfid === campaign.property_sfid) || {};
                const targets = campaign.target?.unit_types || '';
                const targetsList = targets && !targets.includes('All') ? targets.split(';') : [];
                const offersCount = campaign.status === 'Active' ? (campaign.all_actions?.length ?? 0) : 0;

                return {
                    id: campaign.sfid,
                    name: campaign.name || campaign.subtype,
                    community: community.name || '',
                    communitySfid: community.sfid,
                    type: campaign.display_subtype || campaign.subtype,
                    status: campaign.status,
                    offers: offersCount,
                    targets: targetsList
                };
            })
            .sort((a, b) => a.community.localeCompare(b.community));
    }, [campaigns, communities, params.community_sfid]);

    const columns = useMemo(
        () => [
            {field: 'name', headerName: 'Name', width: 250},
            {field: 'community', headerName: 'Community', width: 250},
            {field: 'type', headerName: 'Type', width: 120},
            {
                field: 'status',
                headerName: 'Status',
                width: 120,
                renderCell: (row) => <Badge label={row.status || 'Unknown'} variant={row.status === 'Active' ? 'success' : 'neutral'} />
            },
            {field: 'offers', headerName: 'Offers', width: 120},
            {
                field: 'targets',
                headerName: 'Targets',
                width: 180,
                renderCell: (row) =>
                    row.targets.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            All targets
                        </Typography>
                    ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {row.targets.map((t) => (
                                <Badge key={t} label={t} variant="info" />
                            ))}
                        </Stack>
                    )
            }
        ],
        []
    );

    const handleRowClick = (row) => {
        if (row.communitySfid) {
            navigate(`/account/${params.account_sfid}/community/${row.communitySfid}/campaign/${row.id}`);
        }
    };

    return (
        <DataTableShell
            title="Campaigns"
            description="View and manage your campaigns"
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id}
            searchPlaceholder="Search Campaigns"
            filterable
            onRowClick={handleRowClick}
        />
    );
};

export default CampaignsTable;
